import React, { useState } from 'react';

import { useTheme } from '../context/ThemeContext';

/**
 * The RMC2 brand mark — the one component any surface uses to show the logo.
 *
 * Frameless by decision: the raw artwork as a plain `<img>`, with NO badge,
 * squircle or background fill behind it. Each themed PNG carries its own
 * backdrop matched to the theme, so a frame would show as a hard tile edge.
 *
 * Resolution order, most specific first:
 *   1. `variant` prop            — pins an asset regardless of theme. Used by
 *                                  fixed-palette documents (the printable member
 *                                  ID card, payout and claim vouchers), where a
 *                                  theme-following logo would print a dark tile
 *                                  onto white paper.
 *   2. REACT_APP_LOGO_URL        — one asset for both themes.
 *   3. REACT_APP_LOGO_DARK_URL / REACT_APP_LOGO_LIGHT_URL — per theme.
 *   4. /dark.png · /light.png    — the bundled defaults the operator swaps.
 *   5. /logo192.png              — last-resort fallback, applied on load error
 *                                  so a missing brand asset degrades to the PWA
 *                                  icon instead of a broken-image glyph.
 *
 * Never hotlink a remote logo and never commit a new brand binary: the operator
 * replaces the two PNGs (or sets the envs) once and every surface follows.
 */

const ENV_BOTH = process.env.REACT_APP_LOGO_URL;
const ENV_DARK = process.env.REACT_APP_LOGO_DARK_URL;
const ENV_LIGHT = process.env.REACT_APP_LOGO_LIGHT_URL;

const FALLBACK = '/logo192.png';

function resolveSrc(variant) {
  if (ENV_BOTH) return ENV_BOTH;

  return variant === 'dark'
    ? (ENV_DARK || '/dark.png')
    : (ENV_LIGHT || '/light.png');
}

export default function BrandLogo({
  variant,
  alt = 'RMC2 Community Builders',
  size = 40,
  className = '',
  style,
}) {
  const { isDark } = useTheme();
  const [failed, setFailed] = useState(false);

  const effective = variant ?? (isDark ? 'dark' : 'light');
  const src = failed ? FALLBACK : resolveSrc(effective);

  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      className={className}
      loading="eager"
      decoding="async"
      // The bundled dark.png / light.png are operator-supplied and may not be in
      // place yet. Falling back on error keeps every brand surface rendering
      // something sensible instead of a broken-image icon.
      onError={() => { if (!failed) setFailed(true); }}
      style={{ display: 'block', width: size, height: 'auto', maxWidth: '100%', ...style }}
    />
  );
}
